<template>
    <div>
        <div v-if="!fetchingInstanceUsers" class="d-flex flex-wrap align-center">
            <InstanceInviteDialog :instanceName="currentInstanceName || ''" />
            <v-spacer />
            <v-text-field
                v-model="search"
                :disabled="!instanceUsers.length"
                placeholder="Search for a user..."
                prepend-inner-icon="mdi-magnify"
                solo
                flat
                dense
                hide-details
                clearable>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2" :disabled="!instanceUsers.length">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(filter, name) in roleFilters" :key="name">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch v-model="filter.enabled" inset dense hide-details class="pa-0 ma-0">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ instanceRole(name).icon }}</v-icon>
                                                <span>{{ capitalize(instanceRole(name).label) }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                            <v-divider class="my-2" />
                            <v-sheet rounded class="py-2 px-4 my-1">
                                <v-switch v-model="showInheritedRoles" inset dense hide-details class="pa-0 ma-0">
                                    <template v-slot:label>
                                        <div class="d-flex align-center">
                                            <v-icon left small>mdi-chevron-double-down</v-icon>
                                            <span>Inherited roles</span>
                                        </div>
                                    </template>
                                </v-switch>
                            </v-sheet>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon class="ml-2" @click="fetchUsers"><v-icon>mdi-refresh</v-icon></v-btn>
                </template>
                <span>Refresh</span>
            </v-tooltip>
        </div>

        <v-data-table
            v-if="!fetchingInstanceUsers"
            :headers="headers"
            :items="filteredUsers"
            :items-per-page.sync="itemsPerPage"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            item-key="ucid"
            class="mt-4">
            <template v-slot:[`item.full_name`]="{ item }">
                <div>{{ item.full_name || '-' }}</div>
            </template>
            <template v-slot:[`item.email`]="{ item }">{{ item.email || '-' }}</template>
            <template v-slot:[`item.status`]="{ item }">
                <v-chip outlined label small>
                    <v-icon left x-small :color="item.is_active ? 'success' : 'error'">mdi-circle</v-icon>
                    {{ item.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
            </template>
            <template v-slot:[`item.display_role`]="{ item }">
                <v-tooltip v-if="item.reason !== 'explicit'" bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="secondary" class="mr-2">{{ inheritedRoleIcon(item.reason) }}</v-icon>
                    </template>
                    <span>Inherited from {{ inheritedRoleLabel(item.reason) }}</span>
                </v-tooltip>
                <v-chip outlined label small>
                    <v-icon left small>{{ instanceRole(item.display_role).icon }}</v-icon>
                    {{ capitalize(instanceRole(item.display_role).label) }}
                </v-chip>
            </template>
            <template v-slot:[`item.joined_at`]="{ item }">
                <span v-if="item.joined_at" class="text-no-wrap">{{ item.joined_at | dateTimeToHuman }}</span>
                <span v-else>&mdash;</span>
            </template>
            <template v-slot:[`item.invited_by`]="{ item }">
                <span>{{ item.invited_by || '\u2014' }}</span>
            </template>
            <template v-slot:[`item.invited_at`]="{ item }">
                <span v-if="item.invited_at" class="text-no-wrap">{{ item.invited_at | dateTimeToHuman }}</span>
                <span v-else>&mdash;</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-menu v-if="item.reason === 'explicit'" left offset-x nudge-top="10">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" text color="secondary" small><v-icon>more_horiz</v-icon></v-btn>
                    </template>
                    <v-list nav dense>
                        <InstanceUserRoleDialog
                            :ucid="item.ucid.toString()"
                            :userName="item.full_name"
                            :email="item.email"
                            :instanceId="$route.params.iid.toString()"
                            :instanceName="currentInstanceName || ''"
                            :role="item.role"
                            @role-updated="fetchUsers" />
                        <v-divider class="my-2" />
                        <UserActions
                            :ucid="item.ucid.toString()"
                            :userName="item.full_name"
                            :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                            :instanceId="$route.params.iid.toString()"
                            :instanceName="currentInstanceName"
                            :role="item.role"
                            :reason="item.reason"
                            :spaceAdmin="false"
                            activatorType="listItem" />
                    </v-list>
                </v-menu>
            </template>
            <template v-slot:no-data>
                <span v-if="search">No users found.</span>
                <span v-else>No users have access to this instance.</span>
            </template>
        </v-data-table>
        <v-skeleton-loader v-else type="table" />
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { capitalize } from 'lodash'
import { enumsData } from '@/mixins/enums'
import { instanceRole, userDisplayRole } from '@/utils'
import InstanceInviteDialog from '@/modules/instance/components/InstanceInviteDialog.vue'
import InstanceUserRoleDialog from '@/modules/instance/components/InstanceUserRoleDialog.vue'
const UserActions = () => import('@/components/UserActions.vue')

export default {
    name: 'InstanceUsers',
    components: { InstanceInviteDialog, InstanceUserRoleDialog, UserActions },
    mixins: [enumsData],
    data() {
        return {
            search: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            showInheritedRoles: true,
            roleFilters: {
                OWNER: { enabled: true },
                EDITOR: { enabled: true },
                VIEWER: { enabled: true },
                OBSERVER: { enabled: true }
            },
            headers: [
                { text: 'User', value: 'full_name', sortable: true },
                { text: 'Email', value: 'email', sortable: true },
                { text: 'Status', value: 'status', sortable: false, width: '120px' },
                { text: 'User type', value: 'display_role', sortable: true },
                { text: 'Joined', value: 'joined_at', sortable: true },
                { text: 'Invited by', value: 'invited_by', sortable: true },
                { text: 'Invited on', value: 'invited_at', sortable: true },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '100px' }
            ]
        }
    },
    computed: {
        ...mapState('instanceStore', ['instanceUsers', 'fetchingInstanceUsers']),
        ...mapGetters('instanceStore', ['currentInstanceName']),
        filteredUsers() {
            const term = this.search?.trim().toLowerCase()
            return this.instanceUsers
                .map(user => ({ ...user, display_role: userDisplayRole(user) }))
                .filter(user => {
                    const matchesRole = this.roleFilters[user.display_role]?.enabled
                    const matchesInheritance = user.is_owner || this.showInheritedRoles || user.reason === 'explicit'
                    const matchesSearch =
                        !term || `${user.full_name || ''} ${user.email || ''} ${instanceRole(user.display_role).label}`.toLowerCase().includes(term)
                    return matchesRole && matchesInheritance && matchesSearch
                })
                .sort((firstUser, secondUser) => {
                    const inheritanceOrder = Number(firstUser.reason !== 'explicit') - Number(secondUser.reason !== 'explicit')
                    return inheritanceOrder || (firstUser.full_name || '').localeCompare(secondUser.full_name || '', undefined, { sensitivity: 'base' })
                })
        }
    },
    methods: {
        capitalize,
        instanceRole,
        fetchUsers() {
            return this.$store.dispatch('instanceStore/fetchInstanceUsers', this.$route.params.iid)
        },
        inheritedRoleLabel(reason) {
            return reason === this.roleTypes.SPACE_ADMIN ? 'space administrator' : 'organization manager'
        },
        inheritedRoleIcon(reason) {
            return reason === this.roleTypes.SPACE_ADMIN ? 'mdi-chevron-double-down' : 'mdi-chevron-triple-down'
        }
    }
}
</script>
